import { supabase } from '../lib/supabase';

// Supported ERP Systems in PNG
export const ERP_SYSTEMS = [
  {
    id: 'pronoto',
    name: 'Pronoto ERP',
    description: 'Leading PNG ERP system for manufacturing and distribution',
    supportedModules: ['payroll', 'hr', 'finance', 'inventory'],
    exportFormats: ['CSV', 'XML', 'JSON', 'API'],
    apiEndpoint: '/api/pronoto/sync',
    requiresAuth: true
  },
  {
    id: 'myob',
    name: 'MYOB AccountRight',
    description: 'Popular accounting and payroll system in PNG',
    supportedModules: ['payroll', 'accounting', 'banking'],
    exportFormats: ['CSV', 'Excel', 'API'],
    apiEndpoint: '/api/myob/sync',
    requiresAuth: true
  },
  {
    id: 'xero',
    name: 'Xero',
    description: 'Cloud-based accounting software widely used in PNG',
    supportedModules: ['payroll', 'accounting', 'invoicing'],
    exportFormats: ['CSV', 'API', 'JSON'],
    apiEndpoint: '/api/xero/sync',
    requiresAuth: true
  },
  {
    id: 'sage',
    name: 'Sage Business Cloud',
    description: 'Enterprise resource planning for PNG businesses',
    supportedModules: ['payroll', 'hr', 'finance', 'projects'],
    exportFormats: ['CSV', 'Excel', 'XML', 'API'],
    apiEndpoint: '/api/sage/sync',
    requiresAuth: true
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks Enterprise',
    description: 'Comprehensive business management for PNG companies',
    supportedModules: ['payroll', 'accounting', 'inventory'],
    exportFormats: ['CSV', 'Excel', 'API'],
    apiEndpoint: '/api/quickbooks/sync',
    requiresAuth: true
  },
  {
    id: 'attache',
    name: 'Attaché Software',
    description: 'Australian ERP system used by PNG enterprises',
    supportedModules: ['payroll', 'hr', 'distribution', 'manufacturing'],
    exportFormats: ['CSV', 'Excel', 'XML'],
    apiEndpoint: '/api/attache/sync',
    requiresAuth: true
  }
];

// Field mapping templates for different ERP systems
const FIELD_MAPPINGS = {
  pronoto: {
    employee_id: 'EmployeeCode',
    full_name: 'EmployeeName',
    department: 'DepartmentCode',
    position: 'JobTitle',
    salary: 'BasicSalary',
    start_date: 'EmploymentStartDate',
    tax_file_number: 'TaxFileNumber',
    superannuation_fund: 'SuperFundCode'
  },
  myob: {
    employee_id: 'EmployeeNumber',
    full_name: 'DisplayName',
    department: 'Department',
    position: 'Position',
    salary: 'HourlyRate',
    start_date: 'DateOfBirth',
    tax_file_number: 'TFN'
  },
  xero: {
    employee_id: 'EmployeeID',
    full_name: 'FirstName,LastName',
    department: 'Department',
    position: 'JobTitle',
    salary: 'OrdinaryEarningsRate',
    start_date: 'StartDate',
    tax_file_number: 'TaxFileNumber'
  },
  sage: {
    employee_id: 'EmpID',
    full_name: 'FullName',
    department: 'DeptCode',
    position: 'JobCode',
    salary: 'AnnualSalary',
    start_date: 'HireDate',
    tax_file_number: 'TIN'
  },
  quickbooks: {
    employee_id: 'EmployeeRef',
    full_name: 'DisplayName',
    department: 'Department',
    position: 'Title',
    salary: 'HourlyRate',
    start_date: 'HiredDate',
    tax_file_number: 'SSN'
  },
  attache: {
    employee_id: 'EmpNo',
    full_name: 'EmpName',
    department: 'DeptCode',
    position: 'Position',
    salary: 'Salary',
    start_date: 'StartDate',
    tax_file_number: 'TFN'
  }
};

// Get all ERP systems
export const getERPSystems = () => {
  return ERP_SYSTEMS;
};

// Get ERP system by ID
export const getERPSystemById = (systemId) => {
  return ERP_SYSTEMS?.find(system => system?.id === systemId);
};

// Get field mapping for specific ERP system
export const getFieldMapping = (systemId) => {
  return FIELD_MAPPINGS?.[systemId] || {};
};

// Fetch all ERP integration configurations
export const fetchERPIntegrations = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('portal_configurations')?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching ERP integrations:', error);
    throw error;
  }
};

// Create new ERP integration configuration
export const createERPIntegration = async (integrationData) => {
  try {
    const { data, error } = await supabase?.from('portal_configurations')?.insert([integrationData])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating ERP integration:', error);
    throw error;
  }
};

// Update ERP integration configuration
export const updateERPIntegration = async (integrationId, updates) => {
  try {
    const { data, error } = await supabase?.from('portal_configurations')?.update(updates)?.eq('id', integrationId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating ERP integration:', error);
    throw error;
  }
};

// Delete ERP integration
export const deleteERPIntegration = async (integrationId) => {
  try {
    const { error } = await supabase?.from('portal_configurations')?.delete()?.eq('id', integrationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting ERP integration:', error);
    throw error;
  }
};

// Test ERP connection
export const testERPConnection = async (integrationId) => {
  try {
    const { data, error } = await supabase?.from('portal_configurations')?.select('*')?.eq('id', integrationId)?.single();

    if (error) throw error;

    // Simulate connection test
    const testResult = {
      success: true,
      message: 'Connection successful',
      timestamp: new Date()?.toISOString(),
      latency: Math.floor(Math.random() * 200) + 50
    };

    // Update last tested timestamp
    await supabase?.from('portal_configurations')?.update({ last_tested_at: new Date()?.toISOString() })?.eq('id', integrationId);

    return testResult;
  } catch (error) {
    console.error('Error testing ERP connection:', error);
    return {
      success: false,
      message: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
};

// Fetch export schedules
export const fetchExportSchedules = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('payroll_schedules')?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching export schedules:', error);
    throw error;
  }
};

// Create export schedule
export const createExportSchedule = async (scheduleData) => {
  try {
    const { data, error } = await supabase?.from('payroll_schedules')?.insert([scheduleData])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating export schedule:', error);
    throw error;
  }
};

// Update export schedule
export const updateExportSchedule = async (scheduleId, updates) => {
  try {
    const { data, error } = await supabase?.from('payroll_schedules')?.update(updates)?.eq('id', scheduleId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating export schedule:', error);
    throw error;
  }
};

// Delete export schedule
export const deleteExportSchedule = async (scheduleId) => {
  try {
    const { error } = await supabase?.from('payroll_schedules')?.delete()?.eq('id', scheduleId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting export schedule:', error);
    throw error;
  }
};

// Fetch export history
export const fetchExportHistory = async (companyId, limit = 50) => {
  try {
    const { data, error } = await supabase?.from('error_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching export history:', error);
    throw error;
  }
};

// Execute manual export
export const executeManualExport = async (exportConfig) => {
  try {
    const { integrationId, dataType, format, filters } = exportConfig;

    // Fetch data based on type
    let exportData = [];
    
    if (dataType === 'employees') {
      const { data, error } = await supabase?.from('employees')?.select('*')?.eq('company_id', filters?.companyId);
      
      if (error) throw error;
      exportData = data;
    } else if (dataType === 'payroll') {
      const { data, error } = await supabase?.from('payroll_records')?.select('*')?.eq('company_id', filters?.companyId)?.gte('pay_period_start', filters?.startDate)?.lte('pay_period_end', filters?.endDate);
      
      if (error) throw error;
      exportData = data;
    }

    // Get ERP integration details
    const { data: integration } = await supabase?.from('portal_configurations')?.select('*')?.eq('id', integrationId)?.single();

    // Apply field mapping
    const mapping = getFieldMapping(integration?.erp_system_id);
    const mappedData = exportData?.map(record => {
      const mapped = {};
      Object.keys(mapping)?.forEach(key => {
        mapped[mapping[key]] = record?.[key];
      });
      return mapped;
    });

    // Log export
    const logEntry = {
      company_id: filters?.companyId,
      integration_id: integrationId,
      data_type: dataType,
      export_format: format,
      record_count: mappedData?.length,
      status: 'completed',
      created_at: new Date()?.toISOString()
    };

    await supabase?.from('error_logs')?.insert([logEntry]);

    return {
      success: true,
      data: mappedData,
      recordCount: mappedData?.length,
      format: format
    };
  } catch (error) {
    console.error('Error executing manual export:', error);
    throw error;
  }
};

// Download export file
export const downloadExportFile = (data, format, filename) => {
  let content = '';
  let mimeType = '';

  if (format === 'CSV') {
    const headers = Object.keys(data?.[0] || {})?.join(',');
    const rows = data?.map(row => Object.values(row)?.join(','));
    content = [headers, ...rows]?.join('\n');
    mimeType = 'text/csv';
  } else if (format === 'JSON') {
    content = JSON.stringify(data, null, 2);
    mimeType = 'application/json';
  } else if (format === 'XML') {
    content = '<?xml version="1.0" encoding="UTF-8"?>\n<data>\n';
    data?.forEach(record => {
      content += '  <record>\n';
      Object.entries(record)?.forEach(([key, value]) => {
        content += `    <${key}>${value}</${key}>\n`;
      });
      content += '  </record>\n';
    });
    content += '</data>';
    mimeType = 'application/xml';
  }

  const blob = new Blob([content], { type: mimeType });
  const url = window.URL?.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.${format?.toLowerCase()}`;
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
  window.URL?.revokeObjectURL(url);
};

export default {
  getERPSystems,
  getERPSystemById,
  getFieldMapping,
  fetchERPIntegrations,
  createERPIntegration,
  updateERPIntegration,
  deleteERPIntegration,
  testERPConnection,
  fetchExportSchedules,
  createExportSchedule,
  updateExportSchedule,
  deleteExportSchedule,
  fetchExportHistory,
  executeManualExport,
  downloadExportFile
};

function erpIntegrationService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: erpIntegrationService is not implemented yet.', args);
  return null;
}

export { erpIntegrationService };